(() => {
  if (window.__foxComplementaryCartV3) return;
  window.__foxComplementaryCartV3 = true;

  const controllers = new WeakMap();

  function getProductRoot(form) {
    if (!form || form.tagName !== 'FORM') return null;

    if (
      form.matches('.installment, .product-card-form') ||
      form.closest('.product-card, complementary-product-option')
    ) {
      return null;
    }

    return (
      form.closest('.f-product-single__blocks') ||
      form
        .closest('.f-product-single')
        ?.querySelector('.f-product-single__blocks') ||
      null
    );
  }

  function controllerFor(form) {
    if (!controllers.has(form)) {
      controllers.set(form, new ProductCartController(form));
    }

    return controllers.get(form);
  }

  function createRequest(data) {
    const headers = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    const entries = Array.from(data.entries());

    const indexed = entries.some(([key]) =>
      /^items\[\d+\]\[id\]$/.test(key)
    );

    // Keep multipart submission for file properties.
    if (
      !indexed ||
      entries.some(([, value]) => typeof value !== 'string')
    ) {
      return { headers, body: data };
    }

    const items = new Map();
    const payload = Object.create(null);

    for (const [key, value] of entries) {
      const property = key.match(
        /^items\[(\d+)\]\[properties\]\[(.+)\]$/
      );

      const field = key.match(
        /^items\[(\d+)\]\[([^\[\]]+)\]$/
      );

      if (property || field) {
        const index = Number((property || field)[1]);

        if (!items.has(index)) {
          items.set(index, Object.create(null));
        }

        const item = items.get(index);

        if (property) {
          item.properties ||= Object.create(null);
          item.properties[property[2]] = value;
        } else {
          const name = field[2];

          if (
            ['id', 'quantity', 'parent_id', 'selling_plan'].includes(name)
          ) {
            if (value !== '') {
              item[name] = Number(value);
            }
          } else {
            item[name] = value;
          }
        }
      } else if (key.startsWith('items[')) {
        // Preserve any additional nested data from another integration.
        return { headers, body: data };
      } else {
        const attribute = key.match(/^attributes\[(.+)\]$/);

        if (attribute) {
          payload.attributes ||= Object.create(null);
          payload.attributes[attribute[1]] = value;
        } else {
          payload[key] = value;
        }
      }
    }

    payload.items = Array.from(items.entries())
      .sort(([a], [b]) => a - b)
      .map(([, item]) => item);

    /*
     * When complementary products are selected, force the cart/add payload
     * into this order:
     *   1. Main product
     *   2. Pairs well with / complementary products
     *   3. Any remaining add-ons (personalization, gift ribbon, gift box)
     *
     * The cart drawer also renders by the same group ID, so the visual order
     * remains main product first even if Shopify returns lines differently.
     */
    const complementaryItem = payload.items.find(
      item => item.properties?.['_Complementary Product'] === 'Yes'
    );

    const complementaryGroupId =
      complementaryItem?.properties?.['_Complementary Group ID'];

    if (complementaryGroupId) {
      const mainItem = payload.items.find(
        item =>
          item.properties?.['_Complementary Group ID'] === complementaryGroupId &&
          item.properties?.['_Complementary Product'] !== 'Yes'
      );

      if (mainItem) {
        const complementaryItems = payload.items.filter(
          item =>
            item.properties?.['_Complementary Product'] === 'Yes' &&
            item.properties?.['_Complementary Group ID'] === complementaryGroupId
        );

        const remainingItems = payload.items.filter(
          item => item !== mainItem && !complementaryItems.includes(item)
        );

        payload.items = [
          mainItem,
          ...complementaryItems,
          ...remainingItems
        ];
      }
    }

    if (payload.sections) {
      payload.sections = payload.sections.split(',');
    }

    headers['Content-Type'] = 'application/json';

    return {
      headers,
      body: JSON.stringify(payload)
    };
  }

  class ProductCartController {
    constructor(form) {
      this.form = form;
      this.host = form.closest('product-form') || form;
      this.submitButton = form.querySelector('[name="add"]');
      this.inputId = form.querySelector('[name="id"]');

      this.errorWrapper = this.host.querySelector(
        '.f-product-form__error-message-wrapper'
      );

      this.notificationType = this.host.dataset.notificationType;
      this.Notification = window.FoxTheme?.Notification;
      this.productBlocks = getProductRoot(form);
      this.busy = false;

      if (this.inputId) {
        this.inputId.disabled = false;
      }

      this.initializeRibbonWidget();
      this.syncDynamicCheckout();
    }

    initializeRibbonWidget() {
      const widget = this.productBlocks?.querySelector(
        '[data-source-product-id][data-gift-product-id]'
      );

      if (
        !widget ||
        widget.dataset.productFormControlled === 'true'
      ) {
        return;
      }

      widget.dataset.productFormControlled = 'true';

      const toggle = widget.querySelector('[data-gift-toggle]');
      const details = widget.querySelector('[data-gift-details]');
      const buttonText = widget.querySelector('[data-gift-button-text]');

      toggle?.addEventListener(
        'click',
        event => {
          event.preventDefault();
          event.stopImmediatePropagation();

          const selected = !toggle.classList.contains('is-added');

          toggle.classList.toggle('is-added', selected);
          toggle.setAttribute('aria-pressed', String(selected));
          toggle.setAttribute('aria-expanded', String(selected));

          if (details) {
            details.hidden = !selected;
          }

          if (buttonText) {
            buttonText.textContent = selected
              ? 'Gift Ribbon + Gift Card Added'
              : 'Add Gift Ribbon + Gift Card';
          }

          this.syncDynamicCheckout();
        },
        true
      );

      widget.querySelectorAll('[data-gift-variant]').forEach(button => {
        button.addEventListener(
          'click',
          event => {
            event.preventDefault();
            event.stopImmediatePropagation();

            if (
              button.disabled ||
              button.dataset.available === 'false'
            ) {
              return;
            }

            widget
              .querySelectorAll('[data-gift-variant]')
              .forEach(item => {
                item.classList.toggle('is-selected', item === button);
                item.setAttribute(
                  'aria-checked',
                  String(item === button)
                );
              });

            widget.dataset.selectedVariantId = button.dataset.variantId;

            if (toggle) {
              toggle.dataset.variantId = button.dataset.variantId;
            }
          },
          true
        );
      });
    }

    getComplementarySelections() {
      if (!this.productBlocks) return [];

      // Read the checked rows directly from this product's section.
      // Do not require the checkbox's form attribute to match.
      return Array.from(
        this.productBlocks.querySelectorAll(
          '[data-complementary-checkbox]:checked:not(:disabled)'
        )
      );
    }

    syncDynamicCheckout() {
      if (!this.productBlocks) return;

      const hasAddons =
        this.getComplementarySelections().length > 0 ||
        !!this.productBlocks.querySelector(
          '[data-personalization-toggle]:checked:not(:disabled)'
        ) ||
        !!this.productBlocks.querySelector(
          '[data-gift-box-toggle]:checked:not(:disabled)'
        ) ||
        !!this.productBlocks.querySelector(
          '[data-gift-toggle].is-added'
        );

      this.host.toggleAttribute(
        'data-product-addons-selected',
        hasAddons
      );
    }

    showMessage(message, type = 'warning') {
      const target =
        this.notificationType === 'toast'
          ? document.body
          : this.errorWrapper ||
            this.host.closest('.product-card') ||
            this.host;

      if (target === this.errorWrapper) {
        target.hidden = false;
      }

      if (!this.Notification?.show) {
        let messageNode = this.host.querySelector('[data-cart-result]');

        if (!messageNode) {
          messageNode = document.createElement('p');
          messageNode.setAttribute('data-cart-result', '');
          messageNode.setAttribute(
            'role',
            type === 'warning' ? 'alert' : 'status'
          );
          this.host.appendChild(messageNode);
        }

        messageNode.textContent = message;
        return;
      }

      this.Notification.show({
        target,
        method: 'appendChild',
        type,
        message,
        last: 5000,
        sticky: this.notificationType === 'toast'
      });
    }

    positiveInteger(value, label) {
      const number = Number(value);

      if (!Number.isSafeInteger(number) || number < 1) {
        throw new Error('Please choose a valid ' + label + '.');
      }

      return number;
    }

    prepareCartData(data) {
      const parentId = String(
        this.inputId?.value || data.get('id') || ''
      ).trim();

      this.positiveInteger(parentId, 'product variant');

      const indexes = [
        ...new Set(
          Array.from(data.keys(), key => {
            const match = key.match(/^items\[(\d+)\]/);
            return match ? Number(match[1]) : -1;
          }).filter(index => index >= 0)
        )
      ].sort((a, b) => a - b);

      const mainIndex = indexes.length
        ? indexes.find(
            index =>
              String(data.get('items[' + index + '][id]')) ===
              parentId
          )
        : 0;

      if (mainIndex === undefined) {
        throw new Error(
          'The selected product could not be matched. Please refresh the page and try again.'
        );
      }

      const prefix = 'items[' + mainIndex + ']';

      const quantity = this.positiveInteger(
        data.get(prefix + '[quantity]') ||
          data.get('quantity') ||
          1,
        'quantity'
      );

      const additions = [];
      const properties = new Map();
      const root = this.productBlocks;
      const complementarySelections = this.getComplementarySelections();
      const complementaryGroupId = complementarySelections.length
        ? 'complementary-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
        : '';

      if (complementaryGroupId) {
        properties.set('_Complementary Group ID', complementaryGroupId);
      }

      /*
       * Preserve the personalization fee already added by the
       * product-personalization.liquid formdata listener.
       */
      const personalization = root?.querySelector(
        '[data-personalization]'
      );

      if (
        personalization?.querySelector(
          '[data-personalization-toggle]'
        )?.checked
      ) {
        const feeId =
          personalization.dataset.personalizationFeeVariantId;

        if (
          personalization.dataset.personalizationFeeAvailable !==
            'true' ||
          !feeId
        ) {
          throw new Error(
            'Personalization is temporarily unavailable. Please uncheck it to continue.'
          );
        }

        this.positiveInteger(feeId, 'personalization option');

        const hasFee = indexes.some(
          index =>
            String(data.get('items[' + index + '][id]')) ===
              String(feeId) &&
            String(data.get('items[' + index + '][parent_id]')) ===
              parentId
        );

        if (!hasFee) {
          additions.push({
            id: feeId,
            quantity,
            parent_id: parentId,
            properties: {
              '_Personalization Fee': 'Yes',
              '_Parent Product':
                root
                  .querySelector('.f-product-single__title')
                  ?.textContent.trim() || 'Item',
              '_Parent Variant ID': parentId
            }
          });
        }
      }

      const ribbon = root?.querySelector(
        '[data-source-product-id][data-gift-product-id]'
      );

      const ribbonEnabled = ribbon
        ?.querySelector('[data-gift-toggle]')
        ?.classList.contains('is-added');

      const selectedRibbon = ribbon?.querySelector(
        '[data-gift-variant].is-selected'
      );

      const giftBox = root?.querySelector('[data-gift-box-addon]');

      const giftBoxEnabled = !!giftBox?.querySelector(
        '[data-gift-box-toggle]'
      )?.checked;

      if (ribbonEnabled || giftBoxEnabled) {
        const pairId =
          Date.now() + '-' + Math.random().toString(36).slice(2, 10);

        properties.set('_Gift pair ID', pairId);

        if (ribbonEnabled) {
          if (
            !selectedRibbon ||
            selectedRibbon.disabled ||
            selectedRibbon.dataset.available === 'false'
          ) {
            throw new Error(
              'Please choose an available gift ribbon.'
            );
          }

          const label =
            selectedRibbon.dataset.variantLabel ||
            selectedRibbon.dataset.variantTitle ||
            'Selected';

          properties.set(
            giftBoxEnabled
              ? 'Gift Ribbon Add-on Color'
              : 'Gift Ribbon Color',
            label
          );

          additions.push({
            id: selectedRibbon.dataset.variantId,
            quantity,
            properties: {
              '_Gift ribbon for': ribbon.dataset.sourceProductId,
              'Gift Ribbon Color': label,
              '_Gift pair ID': pairId
            }
          });
        }

        if (giftBoxEnabled) {
          const color =
            giftBox.querySelector('[data-gift-box-color]:checked')
              ?.value || 'Pink';

          properties.set('Gift Box & Gift Card', 'Yes');
          properties.set('Gift Ribbon Color', color);

          additions.push({
            id: giftBox.dataset.giftWrapId,
            quantity,
            properties: {
              'Gift Box & Gift Card For':
                giftBox.dataset.productTitle || 'Item',
              'Gift Ribbon Color': color,
              '_Gift pair ID': pairId
            }
          });
        }
      }

      /*
       * Read the current dropdown selection at submission time.
       * This also works if the checkbox value has not been updated.
       */
      const seen = new Set();

      complementarySelections.forEach(input => {
        const row = input.closest('complementary-product-option');

        const option = row
          ?.querySelector('[data-complementary-variant]')
          ?.selectedOptions[0];

        if (
          option &&
          (option.disabled || option.dataset.available === 'false')
        ) {
          throw new Error(
            'Please choose an available complementary variant.'
          );
        }

        const variantId = String(
          option?.value || input.value
        ).trim();

        if (seen.has(variantId)) return;
        seen.add(variantId);

        additions.push({
          id: variantId,
          quantity: this.positiveInteger(
            option?.dataset.quantity ||
              input.dataset.quantity ||
              1,
            'complementary quantity'
          ),
          properties: {
            '_Complementary Product': 'Yes',
            '_Complementary Parent Variant ID': parentId,
            '_Complementary Group ID': complementaryGroupId
          }
        });
      });

      additions.forEach(item => {
        this.positiveInteger(item.id, 'add-on variant');
      });

      if (!indexes.length && !additions.length) {
        return { data, parentId };
      }

      /*
       * Move only primary product fields into the indexed payload.
       * Preserve existing fee lines, properties and uploaded files.
       */
      Array.from(data.entries()).forEach(([key, value]) => {
        const property = key.match(/^properties\[(.+)\]$/);

        if (property) {
          data.set(
            prefix + '[properties][' + property[1] + ']',
            value
          );
          data.delete(key);
        } else if (
          ['id', 'quantity', 'selling_plan'].includes(key)
        ) {
          data.set(prefix + '[' + key + ']', value);
          data.delete(key);
        }
      });

      data.set(prefix + '[id]', parentId);
      data.set(prefix + '[quantity]', String(quantity));

      properties.forEach((value, name) => {
        data.set(prefix + '[properties][' + name + ']', value);
      });

      let nextIndex = indexes.length
        ? Math.max(...indexes) + 1
        : 1;

      additions.forEach(item => {
        const itemPrefix = 'items[' + nextIndex++ + ']';

        data.append(itemPrefix + '[id]', String(item.id));
        data.append(
          itemPrefix + '[quantity]',
          String(item.quantity)
        );

        if (item.parent_id) {
          data.append(
            itemPrefix + '[parent_id]',
            String(item.parent_id)
          );
        }

        Object.entries(item.properties || {}).forEach(
          ([name, value]) => {
            data.append(
              itemPrefix + '[properties][' + name + ']',
              value
            );
          }
        );
      });

      return { data, parentId };
    }

    async onSubmitHandler(event) {
      event.preventDefault();

      if (
        this.busy ||
        this.submitButton?.disabled ||
        this.submitButton?.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      if (this.errorWrapper) {
        this.errorWrapper.replaceChildren();
        this.errorWrapper.hidden = false;
      }

      this.busy = true;
      this.submitButton?.classList.add('btn--loading');
      this.submitButton?.setAttribute('aria-disabled', 'true');

      let parentId = this.inputId?.value;

      try {
        this.productBlocks = getProductRoot(this.form);
        this.inputId = this.form.querySelector('[name="id"]');

        // Existing personalization formdata listeners still run here.
        const prepared = this.prepareCartData(
          new FormData(this.form)
        );

        const data = prepared.data;
        parentId = prepared.parentId;

        const cartDrawer = document.querySelector('cart-drawer');
        const mainCart = document.querySelector('cart-items');

        const sectionIds = data
          .getAll('sections')
          .flatMap(value => String(value).split(','));

        cartDrawer?.getSectionsToRender?.().forEach(section => {
          sectionIds.push(section.id);
        });

        mainCart?.getSectionsToRender?.().forEach(section => {
          sectionIds.push(section.section);
        });

        const sections = [
          ...new Set(sectionIds.filter(Boolean))
        ].slice(0, 5);

        if (sections.length) {
          data.set('sections', sections.join(','));
          data.set('sections_url', window.location.pathname);
        }

        const request = createRequest(data);

        const result = await fetch(
          window.FoxThemeSettings.routes.cart_add_url,
          {
            method: 'POST',
            headers: request.headers,
            body: request.body
          }
        );

        const response = await result.json();

        if (!result.ok || response.status) {
          this.error = true;

          const message =
            response.description ||
            response.message ||
            'The selected items could not be added.';

          this.showMessage(message);

          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: parentId,
            errors: response.description,
            message: response.message
          });

          return;
        }

        this.error = false;

        // Use the existing theme event to refresh/open the cart.
        try {
          window.FoxThemeEvents.emit('ON_ITEM_ADDED', response);

          const settings = window.FoxThemeSettings.cart;

          if (
            !settings.openDrawerWhenAdded ||
            settings.cartType === 'page'
          ) {
            this.showMessage(
              window.FoxThemeStrings.item_added,
              'success'
            );
          }

          if (settings.cartType === 'page') {
            FoxThemeCartHelpers.getState()
              .then(state => {
                FoxThemeCartHelpers.updateCartCount(
                  state.item_count
                );
              })
              .catch(console.error);
          }
        } catch (error) {
          console.error(error);

          this.showMessage(
            'Items were added. Open your cart to view them.',
            'success'
          );
        }
      } catch (error) {
        console.error(error);
        this.error = true;

        const message =
          error instanceof TypeError ||
          error instanceof SyntaxError
            ? 'We could not confirm the cart update. Please check your cart before trying again.'
            : error.message;

        this.showMessage(message);
      } finally {
        this.busy = false;
        this.submitButton?.classList.remove('btn--loading');
        this.submitButton?.removeAttribute('aria-disabled');
      }
    }
  }

  if (!customElements.get('complementary-product-option')) {
    customElements.define(
      'complementary-product-option',
      class extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;

          this.checkbox = this.querySelector(
            '[data-complementary-checkbox]'
          );

          if (!this.checkbox) return;

          this.initialized = true;

          this.variantSelect = this.querySelector(
            '[data-complementary-variant]'
          );

          this.addEventListener('change', () => this.sync());
          this.sync();
        }

        sync() {
          const option = this.variantSelect?.selectedOptions[0];

          if (option) {
            this.checkbox.value = option.value;
            this.checkbox.dataset.quantity =
              option.dataset.quantity || '1';

            this.checkbox.disabled =
              option.disabled ||
              option.dataset.available !== 'true' ||
              this.dataset.requiresSellingPlan === 'true';

            this.querySelector(
              '[data-complementary-price]'
            ).textContent = option.dataset.price;

            const compare = this.querySelector(
              '[data-complementary-compare]'
            );

            compare.textContent = option.dataset.compare || '';
            compare.hidden = option.dataset.onSale !== 'true';

            const sku = this.querySelector(
              '[data-complementary-sku]'
            );

            sku.textContent = option.dataset.sku
              ? 'SKU: ' + option.dataset.sku
              : '';

            sku.hidden = !option.dataset.sku;

            const image = this.querySelector(
              '[data-complementary-image]'
            );

            if (image && option.dataset.image) {
              image.src = option.dataset.image;
            }

            this.querySelectorAll(
              '[data-complementary-link]'
            ).forEach(link => {
              if (option.dataset.url) {
                link.href = option.dataset.url;
              }
            });
          }

          if (this.checkbox.disabled) {
            this.checkbox.checked = false;
          }

          this.classList.toggle(
            'is-active',
            this.checkbox.checked
          );

          const quantity = Number(
            this.checkbox.dataset.quantity || 1
          );

          const quantityLabel = this.querySelector(
            '[data-complementary-quantity]'
          );

          quantityLabel.textContent = 'Quantity: ' + quantity;
          quantityLabel.hidden = quantity <= 1;

          const root = this.closest(
            '.f-product-single__blocks'
          );

          const owner = root?.querySelector(
            '.f-main-product-form form, form.main-product-form'
          );

          if (owner) {
            controllerFor(owner).syncDynamicCheckout();
          }
        }
      }
    );
  }

  /*
   * ================================================================
   * STICKY ADD TO CART BRIDGE
   * ================================================================
   *
   * Some sticky ATC implementations submit their own form or perform
   * their own cart request outside .f-product-single__blocks. When a
   * Pairs well with checkbox is selected, that would bypass the
   * complementary-product logic above.
   *
   * The bridge below only takes over when at least one complementary
   * checkbox is checked. It then submits the REAL main product form,
   * so the exact same cart payload is used for both the normal ATC and
   * the sticky ATC.
   */

  function findMainProductContext(node) {
    const closestSection = node?.closest?.(
      '.shopify-section, [id^="shopify-section-"]'
    );

    let productSingle = node?.closest?.(
      '.f-product-single[data-section-type="main-product"], .f-product-single'
    );

    if (!productSingle && closestSection) {
      productSingle = closestSection.querySelector(
        '.f-product-single[data-section-type="main-product"], .f-product-single'
      );
    }

    if (!productSingle) {
      const candidates = Array.from(
        document.querySelectorAll(
          '.f-product-single[data-section-type="main-product"], .f-product-single'
        )
      );

      if (candidates.length === 1) {
        productSingle = candidates[0];
      }
    }

    const root = productSingle?.querySelector(
      '.f-product-single__blocks'
    );

    const form = root?.querySelector(
      '.f-main-product-form form, form.main-product-form'
    );

    return { productSingle, root, form };
  }

  function hasSelectedComplementary(root) {
    return !!root?.querySelector(
      '[data-complementary-checkbox]:checked:not(:disabled)'
    );
  }

  function copyStickyFormStateToMain(sourceForm, mainForm) {
    if (!sourceForm || sourceForm === mainForm || !mainForm) {
      return;
    }

    ['id', 'quantity', 'selling_plan'].forEach(name => {
      const source = sourceForm.querySelector(`[name="${name}"]`);
      const target = mainForm.querySelector(`[name="${name}"]`);

      if (!source || !target || source.value === '') return;

      target.disabled = false;
      target.value = source.value;

      target.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    });
  }

  function submitMainFormFromSticky(trigger, sourceForm = null) {
    const context = findMainProductContext(trigger || sourceForm);
    const mainForm = context.form;
    const root = context.root;

    if (!mainForm || !root || !hasSelectedComplementary(root)) {
      return false;
    }

    if (sourceForm && sourceForm === mainForm) {
      return false;
    }

    copyStickyFormStateToMain(sourceForm, mainForm);

    const submitButton = mainForm.querySelector('[name="add"]');

    if (
      !submitButton ||
      submitButton.disabled ||
      submitButton.getAttribute('aria-disabled') === 'true'
    ) {
      return true;
    }

    if (typeof mainForm.requestSubmit === 'function') {
      mainForm.requestSubmit(submitButton);
    } else {
      mainForm.dispatchEvent(
        new SubmitEvent('submit', {
          bubbles: true,
          cancelable: true,
          submitter: submitButton
        })
      );
    }

    return true;
  }

  const stickyAtcSelector = [
    '[data-sticky-atc-button]',
    '#js-sticky-btn',
    'sticky-add-to-cart button',
    'product-sticky-bar button',
    '.sticky-add-to-cart button',
    '.sticky-cart button',
    '.f-sticky-add-to-cart button',
    '.f-product-sticky button',
    '[class*="sticky"][class*="add-to-cart"] button',
    '[class*="sticky"][class*="cart"] button[type="submit"]',
    '[class*="sticky"][class*="cart"] button[name="add"]'
  ].join(',');

  /*
   * Capture sticky button clicks before a sticky component can make
   * its own independent cart request. If no Pairs well with item is
   * selected, the sticky button is left completely untouched.
   */
  document.addEventListener(
    'click',
    event => {
      const trigger = event.target.closest?.(stickyAtcSelector);

      if (!trigger) return;

      const mainContext = findMainProductContext(trigger);

      if (
        !mainContext.form ||
        !hasSelectedComplementary(mainContext.root)
      ) {
        return;
      }

      const sourceForm = trigger.form || trigger.closest('form');

      /*
       * If the sticky button already submits the actual main form,
       * the normal submit listener below will handle everything.
       */
      if (sourceForm === mainContext.form) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      submitMainFormFromSticky(trigger, sourceForm);
    },
    true
  );

  /*
   * This listener is installed even when another theme script has
   * already registered <product-form>.
   *
   * Capture runs before the form's existing submit listeners.
   */
  document.addEventListener(
    'submit',
    event => {
      const form = event.target;
      const root = getProductRoot(form);

      /*
       * Normal main product form submission.
       */
      if (root && hasSelectedComplementary(root)) {
        const action = new URL(
          form.action,
          window.location.href
        );

        if (
          action.origin !== window.location.origin ||
          !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        controllerFor(form).onSubmitHandler(event);
        return;
      }

      /*
       * Separate sticky product form submission.
       * Route it through the real main product form so selected
       * complementary products are included in the same cart request.
       */
      const context = findMainProductContext(form);

      if (
        !context.form ||
        context.form === form ||
        !hasSelectedComplementary(context.root)
      ) {
        return;
      }

      let action;

      try {
        action = new URL(
          form.action || window.FoxThemeSettings?.routes?.cart_add_url || '',
          window.location.href
        );
      } catch (error) {
        return;
      }

      if (
        action.origin !== window.location.origin ||
        !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      submitMainFormFromSticky(form, form);
    },
    true
  );

  document.addEventListener('change', event => {
    const root = event.target.closest?.(
      '.f-product-single__blocks'
    );

    const form = root?.querySelector(
      '.f-main-product-form form, form.main-product-form'
    );

    if (form) {
      controllerFor(form).syncDynamicCheckout();
    }
  });

  /*
   * Provide the normal product-form behavior when this asset owns
   * its registration. Existing theme registrations are supported by
   * the capture listener above.
   */
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;

          this.controller = controllerFor(this.form);
          this.submitButton = this.controller.submitButton;
          this.Notification = this.controller.Notification;
          this.notificationType = this.dataset.notificationType;
          this.type = this.dataset.type;
          this.cartDrawer = document.querySelector('cart-drawer');
          this.mainCart = document.querySelector('cart-items');

          this.domNodes = {
            form: this.form,
            inputId: this.controller.inputId,
            submitButton: this.submitButton,
            errorWrapper: this.controller.errorWrapper
          };

          this._submit ||= this.onSubmitHandler.bind(this);

          this.form.addEventListener('submit', this._submit);
        }

        disconnectedCallback() {
          this.form?.removeEventListener(
            'submit',
            this._submit
          );
        }

        onSubmitHandler(event) {
          if (!event.defaultPrevented) {
            this.controller.onSubmitHandler(event);
          }
        }

        syncDynamicCheckout() {
          this.controller?.syncDynamicCheckout();
        }
      }
    );
  }
})();